import type { AuthState } from '@wehunt/core-web'

/**
 * Whether this account has a password that could be changed.
 *
 * Changing a password reauthenticates with an `EmailAuthProvider` credential,
 * which can only ever succeed for an account carrying the `password` provider.
 * A user who signed up through Google or Apple has no password at all, so the
 * attempt fails as a bad credential — and Firebase reports that the same way it
 * reports a genuinely wrong password. They would be told "that current password
 * isn't correct" about a password that never existed, with no way to proceed.
 * Callers use this to withhold the control rather than offer that dead end.
 *
 * An account can hold several providers: someone who signed up with email and
 * later linked Google still has a password, which is why this looks for the
 * provider rather than comparing against a single primary one.
 */
export function hasPasswordProvider(state: AuthState): boolean {
  if (state.status !== 'signed-in') return false
  // `?? []` covers the e2e build, where a stub User stands in for the real one
  // and a missing providerData would throw rather than simply answer false.
  return (state.user.providerData ?? []).some((p) => p?.providerId === 'password')
}
